import React from 'react';
import PageLayout from '../components/PageLayout';
import TechStack from '../components/TechStack';
import SEOHead from '../components/SEOHead';

const benefits = [
  { icon: '/pictures/faster_cycles_cb82a46bc5.svg', title: 'Faster development cycles', desc: 'Enables faster development cycles by integrating the development, testing and deployment processes, allowing developers to quickly create, test and deploy new features.' },
  { icon: '/pictures/customer_collaboration_2682ad36cc.svg', title: 'Increased collaboration', desc: 'Promotes a culture of collaboration between developers, network engineers and operations teams, leading to improved communication and faster problem resolution.' },
  { icon: '/pictures/improved_quality_841c5f0827.svg', title: 'Improved quality', desc: 'Helps to improve the quality of code by providing continuous testing and feedback throughout the development process, ensuring that bugs and issues are detected and resolved early.' },
  { icon: '/pictures/reduced_downtime_c019ff5c72.svg', title: 'Reduced downtime', desc: 'By automating the deployment and management of infrastructure, it helps to reduce downtime, resulting in improved availability and greater customer satisfaction.' },
  { icon: '/pictures/agile_development_process_11fe77acf4.svg', title: 'Greater agility', desc: 'Allows organizations to quickly respond to changing customer needs and business requirements by enabling rapid development and deployment of new features.' },
  { icon: '/pictures/cost_savings_f78b5b9d76.svg', title: 'Cost savings', desc: 'Helps to reduce costs by automating processes and eliminating manual tasks, reducing the need for additional staff and enabling more efficient use of resources.' },
];

const howWeDo = [
  'Continuous Integration (CI)',
  'Continuous Delivery (CD)',
  'Configuration management',
  'Continuous testing',
  'Monitoring & logging',
];

const whyUs = [
  { icon: '/pictures/interactions_a82e0dc537.svg', title: 'Dedicated DevOps team', desc: 'Our team of experts is highly trained and provides you with the best solutions for your business needs.' },
  { icon: '/pictures/shield_check_7246d46e1b.svg', title: 'Best security firm', desc: 'Our goal is to balance ensuring your business with conducting operations, by implementing a profitable security strategy that considers vulnerabilities and assets.' },
  { icon: '/pictures/intuitive_operation_0da1ab2589.svg', title: 'Intuitive operation', desc: 'Our DevOps shortens release cycles by discovering and eliminating wastes by tracking critical key metrics.' },
];

export default function DevNetOps() {
  return (
    <PageLayout>
      <SEOHead
        title="DevNetOps Services"
        description="Cost-effective DevNetOps services from DISION Tech. Improve code quality, speed delivery with CI/CD, configuration management, and continuous testing."
        keywords="DevNetOps, DevOps, CI/CD, continuous integration, continuous delivery, infrastructure automation, network automation"
        url="/development/dev-net-ops-services"
      />
      <style>{`
        @media (max-width: 768px) {
          .devnetops-intro-grid,
          .devnetops-benefits-grid,
          .devnetops-howwedo-grid,
          .devnetops-whyus-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
      {/* Page Hero */}
      <div style={{ background: '#F9FAFB', padding: '60px 24px 50px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <span style={{ fontSize: '14px', color: '#6B7280' }}>Development</span>
            <span style={{ color: '#D1D5DB' }}>/</span>
            <span style={{ fontSize: '14px', color: '#0099CC', fontWeight: '500' }}>DevNetOps Services</span>
          </div>
          <h1 style={{ fontSize: 'clamp(32px, 4vw, 52px)', fontWeight: '800', color: '#111827', marginBottom: '16px', letterSpacing: '-1px', lineHeight: 1.15 }}>
            DevNetOps Services
          </h1>
          <p style={{ fontSize: '18px', color: '#6B7280', maxWidth: '640px', lineHeight: 1.7 }}>
            DISION Tech provides DevNetOps services at a cost-effective price. We help you implement DevNetOps to improve code quality and speed delivery.
          </p>
        </div>
      </div>

      {/* What is */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div className="devnetops-intro-grid" style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#111827', marginBottom: '24px', letterSpacing: '-0.5px' }}>What are DevNetOps Services?</h2>
            <p style={{ fontSize: '16px', color: '#6B7280', lineHeight: 1.8 }}>
              DevNetOps Services is the implementation of DevOps principles, philosophies, and behaviors to the network operations (NetOps). Our services can help your organizations to improve software delivery speed, quality, and security.
            </p>
          </div>
          <div style={{ borderRadius: '20px', overflow: 'hidden', lineHeight: 0 }}>
            <img src="/pictures/what_Is_Dev_Net_Ops_223915405c.png" alt="DevNetOps" style={{ width: '100%', height: 'auto', display: 'block' }} onError={e => { e.target.src = 'https://images.unsplash.com/photo-1518432031352-d6fc5c10da5a?w=600&q=80'; }} />
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section style={{ padding: '80px 24px', background: '#F9FAFB' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#111827', marginBottom: '12px', letterSpacing: '-0.5px' }}>Benefits of DevNetOps</h2>
          <p style={{ fontSize: '17px', color: '#6B7280', marginBottom: '56px', maxWidth: '500px' }}>Why adopt DevNetOps for your organization.</p>
          <div className="devnetops-benefits-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
            {benefits.map((b, i) => (
              <div key={i} className="service-card" style={{ background: 'white', borderRadius: '16px', padding: '28px', border: '1px solid #E5E7EB' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: '#F0F9FF', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px', padding: '10px' }}>
                  <img src={b.icon} alt={b.title} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </div>
                <h3 style={{ fontSize: '17px', fontWeight: '700', color: '#111827', marginBottom: '10px' }}>{b.title}</h3>
                <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: 1.7, margin: 0 }}>{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How we do it */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div className="devnetops-howwedo-grid" style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#111827', marginBottom: '32px', letterSpacing: '-0.5px' }}>How do we do it?</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {howWeDo.map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 20px', borderRadius: '12px', border: '1px solid #E5E7EB', background: '#F9FAFB' }}>
                  <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: '#0099CC', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                  <span style={{ fontSize: '16px', fontWeight: '500', color: '#374151' }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ borderRadius: '20px', overflow: 'hidden', lineHeight: 0 }}>
            <img src="/pictures/how_we_do_it_f7a18d27ba.jpg" alt="How we do it" style={{ width: '100%', height: 'auto', display: 'block' }} onError={e => { e.target.src = 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=600&q=80'; }} />
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section style={{ padding: '0 24px 80px', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#111827', marginBottom: '40px', letterSpacing: '-0.5px' }}>Why choose us?</h2>
          <div className="devnetops-whyus-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
            {whyUs.map((item, i) => (
              <div key={i} className="service-card" style={{ background: '#F9FAFB', borderRadius: '16px', padding: '32px', border: '1px solid #E5E7EB' }}>
                <div style={{ width: '52px', height: '52px', borderRadius: '12px', background: '#F0F9FF', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px', padding: '10px' }}>
                  <img src={item.icon} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', marginBottom: '12px' }}>{item.title}</h3>
                <p style={{ fontSize: '15px', color: '#6B7280', lineHeight: 1.7, margin: 0 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech stack */}
      <section style={{ padding: '0 24px 80px', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '32px', fontWeight: '700', color: '#111827', marginBottom: '40px', letterSpacing: '-0.5px' }}>Our tech stack</h2>
          <TechStack variant="devnetops" />
        </div>
      </section>
    </PageLayout>
  );
}
